import { useCallback, useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useAuth } from '@auth';
import { type PaymentGatewayFormRef } from '@wallet';
import { formatError } from '@/shared/api';
import { Schedule } from '@/features/accounts/types';
import { type ActivationIntent } from './useScheduleActivation';

interface UseActivationFlowOptions {
  schedule: Schedule | null;
  intent: ActivationIntent;
  onConfirm: (scheduleId: number) => Promise<unknown>;
  onActivated: () => void;
}

const FAILURE_MESSAGE_IDS: Record<ActivationIntent, string> = {
  activate: 'schedule.activate.error',
  reactivate: 'schedule.reactivate.error',
};

export function useActivationFlow({ schedule, intent, onConfirm, onActivated }: UseActivationFlowOptions) {
  const intl = useIntl();
  const { plan } = useAuth();
  const [activationError, setActivationError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isPaymentFormReady, setIsPaymentFormReady] = useState(false);
  const panelRef = useRef<PaymentGatewayFormRef>(null);

  const isOpen = !!schedule;
  const isFunded = schedule?.funded ?? false;
  const costCents = plan?.schedule_cost_cents ?? null;
  const failureMessage = intl.formatMessage({ id: FAILURE_MESSAGE_IDS[intent] });
  const currency = plan?.currency ?? 'USD';

  useEffect(() => {
    if (!isOpen) {
      setActivationError(null);
      setIsSubmitting(false);
      setIsPaymentFormReady(false);
    }
  }, [isOpen]);

  const handlePaymentFormReady = useCallback(() => {
    setIsPaymentFormReady(true);
  }, []);

  const handlePaymentError = useCallback(() => {
    setIsSubmitting(false);
  }, []);

  const handlePaymentConfirmed = useCallback(async () => {
    if (!schedule) return;

    try {
      await onConfirm(schedule.id);
      onActivated();
    } catch (error) {
      setActivationError(formatError(error, failureMessage));
    } finally {
      setIsSubmitting(false);
    }
  }, [schedule, onConfirm, onActivated, failureMessage]);

  const handleConfirm = async () => {
    if (!schedule) return;
    setActivationError(null);

    setIsSubmitting(true);

    if (isFunded) {
      try {
        await onConfirm(schedule.id);
        onActivated();
      } catch (error) {
        setActivationError(formatError(error, failureMessage));
      } finally {
        setIsSubmitting(false);
      }
      return;
    }

    if (panelRef.current) {
      try {
        await panelRef.current.submit();
      } catch {
        setIsSubmitting(false);
      }
    }
  };

  return {
    isSubmitting,
    activationError,
    currency,
    costCents,
    isFunded,
    isPaymentFormReady,
    panelRef,
    handleConfirm,
    handlePaymentFormReady,
    handlePaymentConfirmed,
    handlePaymentError,
  };
}
